import { useOnValueChange } from '@calendly/hooks'
import { I18n } from '@calendly/i18n'
import { Button } from '@calendly/ui/components/button'
import { IconChevronLeft } from '@calendly/ui/components/glyphs/IconChevronLeft'
import { Popup, Header, Title, Body, Footer } from '@calendly/ui/components/popup'
import { UiIcon } from '@calendly/ui/components/ui-icon'
import { CheckboxField } from '@calendly/ui/form/checkbox-field'
import { TextField } from '@calendly/ui/form/text-field'
import { useContext, useState } from 'react'

import ErrorMessage from 'components/core/error_message'

import {
  Subtext,
  HeaderInner,
  BackButton,
  Label,
  ReportReason,
  Container
} from './styles'
import { MeetingDetailDependenciesContext } from '../../meeting_detail_context'


export const ReportSubmitModal = ({
  event,
  reason,
  reasonType,
  onBack,
  onClose,
  onSuccess
}) => {
  const dependencies = useContext(MeetingDetailDependenciesContext)
  if (!dependencies) {
    throw new Error('ReportSubmitModal must be wrapped in a MeetingDetailDependenciesProvider')
  }
  const { useCancelMeetingMutation } = dependencies
  const [reportInfo, setReportInfo] = useState('')
  const [shareMethods, setShareMethods] = useState([])
  const [cancelMeeting, { isLoading, error, isSuccess }] = useCancelMeetingMutation()
  const [errors, setErrors] = useState([])

  useOnValueChange(error, () => {
    if (error.type === 'validation') {
      setErrors(error.errors)
    } else {
      setErrors([])
    }
  })

  useOnValueChange(isSuccess, () => {
    if (isSuccess) {
      onSuccess()
    }
  })

  const onReasonInputChange = ({ target: { value } }) => setReportInfo(value)

  const handleCheckboxClick = (checked, value) => {
    if (checked) {
      setShareMethods([...shareMethods, value])
    } else {
      setShareMethods(shareMethods.filter((x) => x !== value))
    }
  }

  const onConfirm = () => {
    cancelMeeting({
      eventId: event.id,
      reasonType,
      additionalInfo: reportInfo,
      shareMethods
    })
  }

  return (
    <Popup>
      <Header>
        <HeaderInner>
          <BackButton onClick={onBack} aria-label={I18n.t('publisher.action.back')}>
            <UiIcon icon={IconChevronLeft} size="medium" />
          </BackButton>
          <Title>
            {I18n.t('publisher.abuse_reporting.submit_modal.title')}
          </Title>
        </HeaderInner>
      </Header>
      <Body isInverse>
        <Subtext>{I18n.t('publisher.abuse_reporting.reason_modal.subtext')}</Subtext>
        <Label>{I18n.t('publisher.abuse_reporting.submit_modal.reason_section.label')}</Label>
        <ReportReason>{reason}</ReportReason>
        <Label>
          {I18n.t('publisher.abuse_reporting.submit_modal.additional_info_section.label')}
          <span>
            <Label gray>{I18n.t('publisher.abuse_reporting.submit_modal.optional')}</Label>
          </span>
        </Label>
        <TextField
          type="textarea"
          name="report_info"
          value={reportInfo}
          onChange={onReasonInputChange}
          aria-label={I18n.t('publisher.abuse_reporting.submit_modal.additional_info_section.label')}
        />
        <Container>
          <Label>
            {I18n.t('publisher.abuse_reporting.submit_modal.share_section.label')}
            <span>
              <Label gray>{I18n.t('publisher.abuse_reporting.submit_modal.optional')}</Label>
            </span>
          </Label>
          <CheckboxField
            label={I18n.t('publisher.abuse_reporting.submit_modal.share_section.choices.on_website')}
            onChange={(click) => handleCheckboxClick(click, 'website')}
            checked={shareMethods.includes('website')}
          />
          <CheckboxField
            label={I18n.t('publisher.abuse_reporting.submit_modal.share_section.choices.in_ad_campaign')}
            onChange={(click) => handleCheckboxClick(click, 'ad_campaign')}
            checked={shareMethods.includes('ad_campaign')}
          />
          <CheckboxField
            label={I18n.t('publisher.abuse_reporting.submit_modal.share_section.choices.in_email_campaigns')}
            onChange={(click) => handleCheckboxClick(click, 'email_campaign')}
            checked={shareMethods.includes('email_campaign')}
          />
          <CheckboxField
            label={I18n.t('publisher.abuse_reporting.submit_modal.share_section.choices.in_email_signature')}
            onChange={(click) => handleCheckboxClick(click, 'email_signature')}
            checked={shareMethods.includes('email_signature')}
          />
          <CheckboxField
            label={I18n.t('publisher.abuse_reporting.submit_modal.share_section.choices.share_directly')}
            onChange={(click) => handleCheckboxClick(click, 'direct')}
            checked={shareMethods.includes('direct')}
          />
        </Container>
        <ErrorMessage message={errors} />
        <Subtext>
          {I18n.t('publisher.abuse_reporting.submit_modal.subtext')}
        </Subtext>
      </Body>
      <Footer>
        <Button
          disabled={isLoading}
          decoration="secondary-outline"
          onClick={onClose}
        >
          {I18n.t('publisher.action.nevermind')}
        </Button>
        <Button
          loading={isLoading}
          decoration="primary"
          onClick={onConfirm}
        >
          {I18n.t('publisher.action.report_event')}
        </Button>
      </Footer>
    </Popup>
  )
}
